import Wrapper from './wrapper';
import { IWrapper } from './types';
import {
  VendorChangeTicketsApiResponse,
  VendorsApiResponse,
  TVendorStatus,
  VendorChangeTicketEntry,
  VendorType,
  DetectionPattern,
  VendorRenameResponse,
  VendorMergeResponse,
} from '../types/vendor-types';

class Vendor {
  wrapper: IWrapper;

  constructor(wrapper: Wrapper) {
    this.wrapper = wrapper;
  }

  changeTickets(params?: {
    status?: TVendorStatus;
    limit?: number;
    offset?: number;
    document_reviewed?: boolean;
    organization?: string;
    document?: string;
    extraction_batch?: string;
    data_pool?: string;
    normalized_vendor_name?: string;
  }) {
    return this.wrapper.client.get<VendorChangeTicketsApiResponse, VendorChangeTicketsApiResponse>(
      `/vendor-change-tickets`,
      {
        params: {
          status: params?.status ?? 'pending',
          ordering: '-id',
          ...(params?.limit !== undefined ? { limit: params.limit } : {}),
          ...(params?.offset !== undefined ? { offset: params.offset } : {}),
          ...(params?.document_reviewed !== undefined
            ? { document_reviewed: params.document_reviewed }
            : {}),
          ...(params?.organization ? { organization: params.organization } : {}),
          ...(params?.document ? { document: params.document } : {}),
          ...(params?.extraction_batch ? { extraction_batch: params.extraction_batch } : {}),
          ...(params?.data_pool ? { data_pool: params.data_pool } : {}),
          ...(params?.normalized_vendor_name
            ? { normalized_vendor_name: params.normalized_vendor_name }
            : {}),
        },
      }
    );
  }

  updateChangeTicket(id: number, data: { standard_vendor_name: string | null }) {
    return this.wrapper.client.patch<VendorChangeTicketEntry, VendorChangeTicketEntry>(
      `/vendor-change-tickets/${id}`,
      data
    );
  }

  approveChangeTicket(id: number) {
    return this.wrapper.client.post<void, void>(`/vendor-change-tickets/${id}/apply`);
  }

  rejectChangeTicket(id: number) {
    return this.wrapper.client.post<void, void>(`/vendor-change-tickets/${id}/reject`);
  }

  revertRejection(id: number) {
    return this.wrapper.client.post<void, void>(`/vendor-change-tickets/${id}/revert_rejection`);
  }

  getVendors(params?: {
    advanced?: boolean;
    limit?: number;
    offset?: number;
    aliasContains?: string;
  }) {
    return this.wrapper.client.get<VendorsApiResponse, VendorsApiResponse>(`/vendors`, {
      params: {
        ordering: 'name',
        advanced: params?.advanced ?? true,
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
        ...(params?.offset !== undefined ? { offset: params.offset } : {}),
        ...(params?.aliasContains ? { alias_contains: params.aliasContains } : {}),
      },
    });
  }

  deleteVendor(id: number) {
    return this.wrapper.client.delete<void, void>(`/vendors/${id}`);
  }

  updateVendor(
    id: number,
    data: { name?: string; abbreviated?: string; vendor_type?: VendorType }
  ) {
    return this.wrapper.client.patch<void, void>(`/vendors/${id}`, data);
  }

  addVendorAlias(id: number, alias: string) {
    return this.wrapper.client.post<void, void>(`/vendors/${id}/add_alias`, { alias });
  }

  removeVendorAlias(id: number, alias: string) {
    return this.wrapper.client.post<void, void>(`/vendors/${id}/remove_alias`, { alias });
  }

  createVendorDetectionPattern(data: {
    vendor: number;
    label?: string;
    pattern: string;
    pattern_mode?: 'KEYWORD' | 'REGEX';
    case_sensitive?: boolean;
    priority?: number;
  }) {
    return this.wrapper.client
      .post<DetectionPattern, typeof data>(`/vendor-detection-patterns/`, data)
      .then((res) => res as DetectionPattern);
  }

  updateVendorDetectionPattern(
    id: number,
    data: {
      label?: string | null;
      pattern?: string;
      pattern_mode?: 'KEYWORD' | 'REGEX';
      case_sensitive?: boolean;
      priority?: number;
    }
  ) {
    return this.wrapper.client.patch<void, void>(`/vendor-detection-patterns/${id}/`, data);
  }

  deleteVendorDetectionPattern(id: number) {
    return this.wrapper.client.delete<void, void>(`/vendor-detection-patterns/${id}/`);
  }

  exportCsv() {
    return this.wrapper.client.get<Blob, Blob>(`/vendors/export_csv/`, {
      responseType: 'blob' as any,
    });
  }

  renameVendor(id: number, newName: string) {
    return this.wrapper.client.post<VendorRenameResponse, { new_name: string }>(
      `/vendors/${id}/rename`,
      { new_name: newName }
    );
  }

  mergeVendor(sourceId: number, targetVendorId: number) {
    return this.wrapper.client.post<VendorMergeResponse, { target_vendor_id: number }>(
      `/vendors/${sourceId}/merge`,
      { target_vendor_id: targetVendorId }
    );
  }
}

export default Vendor;
